import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, HttpError, performLogout, setUnauthorizedHandler } from "@/lib/api";
import type { MeResponse } from "@/lib/types";

type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "denied"; reason?: string }
  | { status: "authenticated"; user: MeResponse };

interface AuthCtx {
  state: AuthState;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const Context = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  const refresh = async () => {
    try {
      const me = await api.get<MeResponse>("/auth/me");
      setState({ status: "authenticated", user: me });
    } catch (e) {
      if (e instanceof HttpError) {
        if (e.status === 401) {
          setState({ status: "anonymous" });
        } else if (e.status === 403) {
          setState({ status: "denied", reason: e.message });
        } else {
          setState({ status: "anonymous" });
        }
      } else {
        setState({ status: "anonymous" });
      }
    }
  };

  const logout = async () => {
    // pilgrims 모드에선 공유 쿠키까지 만료시켜야 완전 로그아웃(그 뒤 페이지 이동).
    // performLogout 이 세션 파기 → pilgrims 로그아웃/로그인 화면으로 리다이렉트한다.
    setState({ status: "anonymous" });
    await performLogout();
  };

  useEffect(() => {
    setUnauthorizedHandler(() => {
      // 401 응답이 오면 anonymous 상태로 전환 (라우트가 LoginPage 로 자연 이동)
      setState({ status: "anonymous" });
    });
    refresh();
  }, []);

  return (
    <Context.Provider value={{ state, refresh, logout }}>
      {children}
    </Context.Provider>
  );
}

export function useAuth(): AuthCtx {
  const v = useContext(Context);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}
